/**
 * PdfViewersTab – protocol PDF viewer with redaction.
 *
 * Uses the same pdfjs-dist version as ClinicalIntakeForm / PdfJsHighlightViewer
 * / PdfJsSimpleViewer (all resolve to one npm dependency) so there is ONE
 * pdfjs version and ONE worker in the entire app — pdfjs-dist throws an
 * "API version X does not match Worker version Y" error at document-load
 * time if the API and worker builds disagree, which is also why react-pdf
 * (5.3.31, which bundles its own separate pdfjs-dist copy) is intentionally
 * NOT used here.
 *
 * Redaction: click and drag on any page to draw a black redaction box.
 * Redactions are saved to the backend on every change and restored on reload.
 */
import React, { useEffect, useRef, useState, useCallback } from 'react';
import * as pdfjsLib from 'pdfjs-dist';
import {
  Box,
  Button,
  Typography,
  Alert,
  CircularProgress,
  Stack,
  IconButton,
  Tooltip,
  Paper,
  Chip,
} from '@mui/material';
import {
  Block as BlockIcon,
  Delete as DeleteIcon,
  ArrowBack as ArrowBackIcon,
  ArrowForward as ArrowForwardIcon,
  ZoomIn as ZoomInIcon,
  ZoomOut as ZoomOutIcon,
  SaveAlt as SaveAltIcon,
  OpenInNew as OpenInNewIcon,
  Download as DownloadIcon,
  CheckCircle as CheckCircleIcon,
Lightbulb as LightbulbIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
} from '@mui/icons-material';
import api from '../../services/api';
import {
  FormControlLabel,
  Checkbox,
  Collapse,
  TextField,
} from '@mui/material';

// Not `new URL('pdfjs-dist/...', import.meta.url)` — react-scripts' Babel pass
// over bundled .mjs files injects absolute-filesystem-path helper imports
// that break once emitted as a standalone asset (see PdfJsHighlightViewer.jsx).
// scripts/copy-pdf-worker.js (postinstall) copies the worker into public/
// untouched instead.
pdfjsLib.GlobalWorkerOptions.workerSrc = `${process.env.PUBLIC_URL}/pdf.worker.min.mjs`;

const REDACTION_COLOR = '#1a1a1a';


/** Default redaction strategy context (DAIICHI SANKYO & PARTNER). */
const DEFAULT_REDACTION_CONTEXT = `DAIICHI SANKYO & PARTNER CLINICAL PROTOCOL REDACTION STRATEGY

This document specifies the highly secure redaction strategy for clinical trial protocols,
covering Personal Protected Data (PPD) and Commercially Confidential Information (CCI).
The strategy extends into granular study operations, proprietary science, and
statistical triggers to ensure global regulatory compliance and IP protection.

PERSONAL PROTECTED DATA (PPD)
Ensures compliance with global privacy regulations (GDPR, HIPAA) by removing
personally identifiable information (PII).

INVESTIGATORS:
Names, job titles, and institution names.
Signatures of Principal Investigators, Co-investigators, or Sub-investigators.

SPONSOR SIGNATORIES:
Names and signatures of approving Sponsor Representatives.
Specific job titles/functions (e.g., Clinical Development Leads, Regulatory Leads).

MEDICAL MONITORS:
Individual names, direct email addresses, and direct phone numbers.
Physical office addresses of assigned monitors.
NOTE: General toll-free 24/7 emergency hotline numbers remain visible.

PROPRIETARY SCIENCE & CLINICAL PHARMACOLOGY (CCI)
Protects Intellectual Property regarding drug mechanism, metabolism, and stability.

PHARMACOKINETIC (PK) PARAMETERS:
Exact terminal half-life estimates.
Maximum observed concentration (Cmax) timings.
Drug accumulation estimates after repeated dosing.

PRECLINICAL / NONCLINICAL FINDINGS:
Detailed text descriptions, charts, and figures.
Unpublished pharmacology studies (e.g., xenograft models, in-vivo stability).
Synergistic mechanisms of action data.

UNPUBLISHED CLINICAL DATA FROM PRIOR/ONGOING TRIALS (CCI)
EXACT PATIENT COHORT SIZES:
Specific number ("N") of efficacy-evaluable or safety-evaluable patients.

SPECIFIC EFFICACY RATES:
Exact percentages for ORR, CR, or PFS in niche subgroups.
Data related to specific gene mutations or expression levels.

SPECIFIC SAFETY STATISTICS:
Exact percentages/counts for Treatment-Emergent Adverse Events (TEAEs).
Data on Grade 3+ TEAEs or events leading to death/discontinuation.

INVESTIGATIONAL ASSAYS, BIOMARKERS, AND PARTNERSHIPS (CCI)
PROPRIETARY SCORING ALGORITHMS:
Unpublished guidelines or methodologies used to score biomarkers.
Specific IHC scoring cut-offs.

INVESTIGATIONAL DEVICES:
Exact names, manufacturers, or designations of IVD tests.
Details on companion diagnostics used for patient selection.

COMMERCIAL PARTNERS:
Names of third-party diagnostic partners or co-development entities.

BIOMARKER TESTING TIMELINES:
Procedural timeframes (weeks/months) for sample collection (e.g., MRD).
Clonal evolution monitoring schedules.

HIGHLY CONSERVATIVE OPERATIONAL TIMELINES & VISIT WINDOWS (CCI)
OVERALL TRIAL DURATION:
Projected total study duration and individual treatment duration.

PROCEDURAL TIMEFRAMES:
Exact number of days allowed for the Screening window.
Maximum days between randomization and first dose.
Exact definition of a treatment cycle (e.g., "A cycle is exactly [X] days").

FOLLOW-UP WINDOWS:
EOT/Safety Follow-up visit timeframes.
Frequency of long-term survival follow-up contacts.

SAFETY MONITORING INTERVALS:
Exact frequency of Independent Data Monitoring Committee (IDMC) meetings.

DOSE MODIFICATION LIMITS:
Maximum allowed time limit for dose interruptions before discontinuation.

RADIOGRAPHIC CONFIRMATION WINDOWS:
Specific day-windows for imaging to confirm suspected disease progression.

STATISTICAL STRATEGY & ANALYSIS TRIGGERS (CCI)
STRATIFICATION CAPS/TARGETS:
Exact percentages of the population targeted for specific factors.

INTERIM ANALYSIS TRIGGERS:
Exact percentage or "information fraction" required to trigger analysis.
Example: "When approximately [X]% of events have been observed."

TARGET EVENT COUNTS:
Specific numerical targets for events (PFS/OS) required for final analysis.

===============================================================================
END OF SPECIFICATION`;

/** Extract full text from a PDF page (for LLM / phrase search). */
async function getPageText(pdfPage) {
  const content = await pdfPage.getTextContent();
  const items = content.items || [];
  return items.map((it) => (it.str || '')).join(' ');
}

/** Build normalized string from fullText and normToOrig[i] = original char index for normStr[i]. */
function buildNormMapping(fullText) {
  const normStr = [];
  const normToOrig = [];
  let i = 0;
  while (i < fullText.length) {
    if (/\s/.test(fullText[i])) {
      normStr.push(' ');
      normToOrig.push(i);
      while (i < fullText.length && /\s/.test(fullText[i])) i++;
      continue;
    }
    const start = i;
    while (i < fullText.length && !/\s/.test(fullText[i])) i++;
    const word = fullText.slice(start, i).toLowerCase();
    for (let k = 0; k < word.length; k++) {
      normStr.push(word[k]);
      normToOrig.push(start + k);
    }
  }
  return { normStr: normStr.join(''), normToOrig };
}

/**
 * Get bounding box in PDF page coordinates (origin bottom-left, y-up) for a text item.
 * transform[4]=x, transform[5]=y (baseline); width/height are the run dimensions.
 * Use baseline as bottom and baseline+height as top so the box squarely wraps the text.
 */
function getItemBbox(it) {
  const t = it.transform || [1, 0, 0, 1, 0, 0];
  const w = Math.max(0, it.width != null ? it.width : 0);
  const h = Math.max(0, it.height != null ? it.height : 0);
  const x = t[4];
  const y = t[5];
  return {
    left: x,
    right: x + w,
    bottom: y,
    top: y + h,
  };
}

/** Find all normalized rects (0–1, top-left origin) for a phrase on a page using text layer. */
async function phraseToRectsOnPage(pdfPage, phrase) {
  if (!phrase || !String(phrase).trim()) return [];
  const content = await pdfPage.getTextContent();
  const items = content.items || [];
  const fullText = items.map((it) => (it.str || '')).join('');
  const search = String(phrase).trim();
  const rects = [];
  const vp = pdfPage.getViewport({ scale: 1 });
  const pageW = vp.width;
  const pageH = vp.height;
  if (pageW <= 0 || pageH <= 0) return [];

  const norm = (t) => t.replace(/\s+/g, ' ').trim().toLowerCase();
  const searchNorm = norm(search);
  if (!searchNorm) return [];

  const { normStr, normToOrig } = buildNormMapping(fullText);
  const itemStarts = [];
  let acc = 0;
  for (let i = 0; i < items.length; i++) {
    itemStarts.push(acc);
    acc += (items[i].str || '').length;
  }

  let idx = normStr.indexOf(searchNorm);
  while (idx !== -1) {
    const endNormIdx = idx + searchNorm.length;
    const origStart = normToOrig[idx] ?? 0;
    const origEnd = endNormIdx <= normToOrig.length ? (normToOrig[endNormIdx - 1] ?? 0) + 1 : fullText.length;
    let startItem = 0;
    for (let i = 0; i < itemStarts.length; i++) {
      if (itemStarts[i] <= origStart) startItem = i;
    }
    let endItem = startItem;
    for (let i = 0; i < itemStarts.length; i++) {
      if (itemStarts[i] < origEnd) endItem = i;
    }
    endItem = Math.min(endItem, items.length - 1);

    let left = Infinity, bottom = Infinity, right = -Infinity, top = -Infinity;
    for (let i = startItem; i <= endItem; i++) {
      const b = getItemBbox(items[i]);
      left = Math.min(left, b.left);
      right = Math.max(right, b.right);
      bottom = Math.min(bottom, b.bottom);
      top = Math.max(top, b.top);
    }
    if (left !== Infinity && right > left && top > bottom) {
      const widthPdf = right - left;
      const reduceFromTopPt = 2;
      const extendDownPt = 14;
      const topPdf = top - reduceFromTopPt;
      const bottomPdf = bottom - extendDownPt;
      const heightPdf = topPdf - bottomPdf;
      const xNorm = Math.max(0, Math.min(1, left / pageW));
      const widthNorm = Math.max(0, Math.min(1 - xNorm, widthPdf / pageW));
      const topNorm = Math.max(0, 1 - (topPdf / pageH));
      const heightNorm = Math.max(0, Math.min(1 - topNorm, heightPdf / pageH));
      rects.push({
        x: xNorm,
        y: topNorm,
        width: widthNorm,
        height: heightNorm,
      });
    }
    idx = normStr.indexOf(searchNorm, idx + 1);
  }
  return rects;
}
const DEFAULT_SCALE = 1.5;
const SCALE_STEP = 0.25;
const SCALE_MIN = 0.5;
const SCALE_MAX = 3;

// ─── single page canvas + redaction overlay ────────────────────────────────
const PdfPage = ({
  pdf,
  pageNum,
  scale,
  pageRedactions,
  drawing,
  drawingPage,
  currentRect,
  onMouseDown,
  onMouseMove,
  onMouseUp,
  onDeleteRedaction,
}) => {
  const canvasRef = useRef();
  const containerRef = useRef();
  const renderTaskRef = useRef(null);
  const [dims, setDims] = useState({ w: 0, h: 0 });

  useEffect(() => {
    if (renderTaskRef.current?.cancel) renderTaskRef.current.cancel();
    if (!pdf) return;
    let cancelled = false;
    (async () => {
      try {
        const page = await pdf.getPage(pageNum);
        const vp = page.getViewport({ scale });
        if (cancelled) return;
        setDims({ w: vp.width, h: vp.height });
        const canvas = canvasRef.current;
        if (!canvas) return;
        canvas.width = vp.width;
        canvas.height = vp.height;
        renderTaskRef.current = page.render({ canvasContext: canvas.getContext('2d'), viewport: vp });
        await renderTaskRef.current.promise;
      } catch (_) {}
    })();
    return () => {
      cancelled = true;
      if (renderTaskRef.current?.cancel) renderTaskRef.current.cancel();
    };
  }, [pdf, pageNum, scale]);

  const rects = pageRedactions?.rects || [];
  const isDrawingHere = drawing && drawingPage === pageNum;

  return (
    <Box
      ref={containerRef}
      sx={{
        position: 'relative',
        width: dims.w || 'auto',
        height: dims.h || 'auto',
        margin: '16px auto',
        boxShadow: 2,
        bgcolor: 'white',
        cursor: 'crosshair',
        userSelect: 'none',
      }}
      onMouseDown={(e) => onMouseDown(e, pageNum, containerRef.current)}
      onMouseMove={(e) => onMouseMove(e, pageNum, containerRef.current)}
      onMouseUp={(e) => onMouseUp(e, pageNum, containerRef.current)}
    >
      <canvas ref={canvasRef} style={{ display: 'block' }} />

      {/* Saved redaction boxes */}
      {rects.map((r, i) => (
        <Box
          key={i}
          sx={{
            position: 'absolute',
            left: `${r.x * 100}%`,
            top: `${r.y * 100}%`,
            width: `${r.width * 100}%`,
            height: `${r.height * 100}%`,
            bgcolor: REDACTION_COLOR,
            zIndex: 2,
            '&:hover .del-btn': { display: 'flex' },
          }}
        >
          <Box
            className="del-btn"
            sx={{
              display: 'none',
              position: 'absolute',
              top: -14,
              right: -14,
              zIndex: 3,
            }}
          >
            <Tooltip title="Remove redaction">
              <IconButton
                size="small"
                sx={{ bgcolor: 'error.main', color: 'white', p: '2px', '&:hover': { bgcolor: 'error.dark' } }}
                onMouseDown={(e) => e.stopPropagation()}
                onClick={(e) => { e.stopPropagation(); onDeleteRedaction(pageNum, i); }}
              >
                <DeleteIcon sx={{ fontSize: 14 }} />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>
      ))}

      {/* Live drawing preview */}
      {isDrawingHere && currentRect && (
        <Box
          sx={{
            position: 'absolute',
            left: `${currentRect.x * 100}%`,
            top: `${currentRect.y * 100}%`,
            width: `${currentRect.width * 100}%`,
            height: `${currentRect.height * 100}%`,
            bgcolor: 'rgba(26,26,26,0.55)',
            border: '2px dashed #555',
            zIndex: 4,
            pointerEvents: 'none',
          }}
        />
      )}

      {/* Page label */}
      <Box sx={{
        position: 'absolute', bottom: 4, right: 8,
        bgcolor: 'rgba(0,0,0,0.35)', color: '#fff',
        fontSize: 11, px: 0.75, py: 0.25, borderRadius: 1, zIndex: 1,
        pointerEvents: 'none',
      }}>
        {pageNum}
      </Box>
    </Box>
  );
};

// ─── main component ─────────────────────────────────────────────────────────
const PdfViewersTab = ({ projectId }) => {
  const [pdf, setPdf] = useState(null);
  const [numPages, setNumPages] = useState(0);
  const [scale, setScale] = useState(DEFAULT_SCALE);
  const [loading, setLoading] = useState(false);
  const [loadingRedactions, setLoadingRedactions] = useState(false);
  const [error, setError] = useState(null);
  const [redactions, setRedactions] = useState([]); // [{ pageIndex, rects:[{x,y,width,height}] }
  const [redactionsLoaded, setRedactionsLoaded] = useState(false); // guard: don't save until loaded
  const [saving, setSaving] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);

  // Burned redacted PDF state
  const [burning, setBurning] = useState(false);
  const [burnError, setBurnError] = useState(null);
  const [redactedPdfUrl, setRedactedPdfUrl] = useState(null);
  const [redactedAt, setRedactedAt] = useState(null);

  // Drawing state
  const [drawing, setDrawing] = useState(false);
  const [drawingPage, setDrawingPage] = useState(null);
  const [drawStart, setDrawStart] = useState(null);
  const [currentRect, setCurrentRect] = useState(null);

   // LLM Suggestion panel
  const [llmSuggestionOpen, setLlmSuggestionOpen] = useState(false);
  const [llmContext, setLlmContext] = useState(DEFAULT_REDACTION_CONTEXT);
  const [suggestions, setSuggestions] = useState([]);
  const [selectedSuggestionIds, setSelectedSuggestionIds] = useState(new Set());
  const [loadingSuggestions, setLoadingSuggestions] = useState(false);
  const [applyingSuggestions, setApplyingSuggestions] = useState(false);
  const [suggestionError, setSuggestionError] = useState(null);


  const scrollRef = useRef();
  const saveTimerRef = useRef(null);

  // ── load PDF ──────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!projectId) { setPdf(null); setError(null); return; }
    setLoading(true);
    setError(null);
    setPdf(null);

    api
      .get(`/export/protocol-pdf/${projectId}`, { responseType: 'arraybuffer' })
      .then((res) => {
        const data = res.data;
        if (!data || (data instanceof ArrayBuffer && data.byteLength === 0)) {
          throw new Error('Empty PDF response');
        }
        const uint8 = new Uint8Array(data);
        return pdfjsLib.getDocument({ data: uint8, isEvalSupported: false }).promise;
      })
      .then((loadedPdf) => {
        setPdf(loadedPdf);
        setNumPages(loadedPdf.numPages);
        setCurrentPage(1);
      })
      .catch((err) => {
        setPdf(null);
        const status = err?.response?.status;
        setError(
          status === 404
            ? 'No protocol PDF found. Export protocol as PDF (Native) from the Protocol Editor first.'
            : err?.response?.data?.message || err?.message || 'Failed to load PDF.',
        );
      })
      .finally(() => setLoading(false));
  }, [projectId]);

  // ── load redactions ───────────────────────────────────────────────────────
  useEffect(() => {
    if (!projectId) { setRedactions([]); setRedactionsLoaded(false); return; }
    setRedactionsLoaded(false);
    setLoadingRedactions(true);
    api
      .get(`/export/protocol-pdf-redactions/${projectId}`)
      .then((res) => {
        if (res.data?.success && Array.isArray(res.data.redactions)) {
          setRedactions(res.data.redactions);
        }
      })
      .catch(() => {})
      .finally(() => {
        setLoadingRedactions(false);
        setRedactionsLoaded(true); // only now allow auto-save to run
      });
  }, [projectId]);

  // ── load existing burned redacted PDF info ────────────────────────────────
  useEffect(() => {
    if (!projectId) { setRedactedPdfUrl(null); setRedactedAt(null); return; }
    api
      .get(`/export/redacted-pdf-url/${projectId}`)
      .then((res) => {
        if (res.data?.success && res.data?.url) {
          setRedactedPdfUrl(res.data.url);
          setRedactedAt(res.data.redactedAt ? new Date(res.data.redactedAt) : null);
        }
      })
      .catch(() => { setRedactedPdfUrl(null); setRedactedAt(null); });
  }, [projectId]);

  // ── build redacted PDF (selective rasterization) ──────────────────────────
  // Only pages that have redactions drawn on them are rasterized to images.
  // All other pages keep the original vector PDF content (text stays selectable,
  // searchable, and copyable on clean pages).
  //
  // How it works:
  //   1. For each page WITH redactions: render to offscreen canvas via pdfjs,
  //      paint solid black rectangles onto the canvas pixels (content destroyed),
  //      export as JPEG base64.
  //   2. Send only those JPEG images + projectId to the backend as JSON.
  //   3. Backend uses pdf-lib to build a hybrid PDF:
  //        • non-redacted pages  → copied verbatim from the original (vector intact)
  //        • redacted pages      → replaced with the JPEG image (no text layer)
  //   4. Backend saves to Azure and returns a SAS URL.
  const handleBurnRedactedPdf = useCallback(async () => {
    if (!projectId || !pdf) return;
    setBurning(true);
    setBurnError(null);
    try {
      const RENDER_SCALE = 2.0; // 2× resolution for crisp rasterized output

      // Find which pages actually have redactions (1-based pageIndex)
      const redactedPageNums = new Set(redactions.map((r) => r.pageIndex));

      if (redactedPageNums.size === 0) {
        throw new Error('No redactions drawn yet. Draw redaction boxes first, then click Generate & Save.');
      }

      // Render only the redacted pages to JPEG
      const redactedPageImages = [];
      for (const pageNum of Array.from(redactedPageNums).sort((a, b) => a - b)) {
        const page = await pdf.getPage(pageNum);
        const vp = page.getViewport({ scale: RENDER_SCALE });

        const canvas = document.createElement('canvas');
        canvas.width = Math.round(vp.width);
        canvas.height = Math.round(vp.height);
        const ctx = canvas.getContext('2d');

        // Render the PDF page — captures all text/image content as pixels
        await page.render({ canvasContext: ctx, viewport: vp }).promise;

        // Paint solid black rectangles — destroys the content at pixel level
        const entry = redactions.find((r) => r.pageIndex === pageNum);
        if (entry?.rects?.length) {
          ctx.fillStyle = '#000000';
          for (const r of entry.rects) {
            ctx.fillRect(
              r.x * canvas.width,
              r.y * canvas.height,
              r.width * canvas.width,
              r.height * canvas.height,
            );
          }
        }

        // Strip the data:image/jpeg;base64, prefix before sending
        const dataUrl = canvas.toDataURL('image/jpeg', 0.92);
        const base64 = dataUrl.replace(/^data:image\/jpeg;base64,/, '');

        redactedPageImages.push({
          pageIndex: pageNum,          // 1-based
          imageBase64: base64,
          widthPt: vp.width / RENDER_SCALE,   // PDF points at scale 1
          heightPt: vp.height / RENDER_SCALE,
        });
      }

      // Send to backend: only the rasterized pages + projectId
      // Server fetches the original PDF, copies clean pages verbatim,
      // and swaps redacted pages with our images
      const res = await api.post('/export/build-redacted-pdf', {
        projectId,
        redactedPageImages,
      });

      if (res.data?.success && res.data?.url) {
        setRedactedPdfUrl(res.data.url);
        setRedactedAt(new Date());
      } else {
        throw new Error(res.data?.message || 'Upload failed');
      }
    } catch (e) {
      setBurnError(e.response?.data?.message || e.message || 'Failed to generate redacted PDF');
    } finally {
      setBurning(false);
    }
  }, [projectId, pdf, redactions]);

  // ── download redacted PDF directly via our API ────────────────────────────
  const handleDownloadRedactedPdf = useCallback(async () => {
    if (!projectId) return;
    try {
      // Use the axios instance so auth headers are included and the baseURL is
      // applied correctly regardless of its format (avoids double /api prefix).
      const res = await api.get(`/export/redacted-pdf/${projectId}`, {
        responseType: 'blob',
      });
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${projectId}_redacted_protocol.pdf`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    } catch (e) {
      setBurnError(e.response?.data?.message || e.message || 'Failed to download redacted PDF');
    }
  }, [projectId]);

  // ── save redactions (debounced) ───────────────────────────────────────────
  const persistRedactions = useCallback(async (list) => {
    if (!projectId) return;
    setSaving(true);
    try {
      await api.put(`/export/protocol-pdf-redactions/${projectId}`, { redactions: list });
    } catch (e) {
      console.error('Failed to save redactions', e);
    } finally {
      setSaving(false);
    }
  }, [projectId]);

  useEffect(() => {
    if (!projectId || !redactionsLoaded) return;
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    saveTimerRef.current = setTimeout(() => persistRedactions(redactions), 800);
    return () => clearTimeout(saveTimerRef.current);
  }, [redactions, projectId, persistRedactions, redactionsLoaded]);

  // ── drawing handlers ──────────────────────────────────────────────────────
  const getNormCoords = (e, container) => {
    if (!container) return null;
    const cr = container.getBoundingClientRect();
    return {
      x: Math.max(0, Math.min(1, (e.clientX - cr.left) / cr.width)),
      y: Math.max(0, Math.min(1, (e.clientY - cr.top) / cr.height)),
    };
  };

  const handleMouseDown = (e, pageNum, container) => {
    if (e.button !== 0) return;
    const coords = getNormCoords(e, container);
    if (!coords) return;
    setDrawing(true);
    setDrawingPage(pageNum);
    setDrawStart(coords);
    setCurrentRect(null);
  };

  const handleMouseMove = (e, pageNum, container) => {
    if (!drawing || drawingPage !== pageNum || !drawStart) return;
    const coords = getNormCoords(e, container);
    if (!coords) return;
    setCurrentRect({
      x: Math.min(drawStart.x, coords.x),
      y: Math.min(drawStart.y, coords.y),
      width: Math.abs(coords.x - drawStart.x),
      height: Math.abs(coords.y - drawStart.y),
    });
  };

  const handleMouseUp = (e, pageNum, container) => {
    if (!drawing || drawingPage !== pageNum) { setDrawing(false); return; }
    const coords = getNormCoords(e, container);
    if (coords && drawStart && currentRect && currentRect.width > 0.005 && currentRect.height > 0.005) {
      const rect = {
        x: Math.min(drawStart.x, coords.x),
        y: Math.min(drawStart.y, coords.y),
        width: Math.abs(coords.x - drawStart.x),
        height: Math.abs(coords.y - drawStart.y),
      };
      setRedactions((prev) => {
        const next = prev.filter((r) => r.pageIndex !== pageNum);
        const existing = prev.find((r) => r.pageIndex === pageNum);
        return [...next, { pageIndex: pageNum, rects: [...(existing?.rects || []), rect] }].sort(
          (a, b) => a.pageIndex - b.pageIndex,
        );
      });
    }
    setDrawing(false);
    setDrawingPage(null);
    setDrawStart(null);
    setCurrentRect(null);
  };

  const handleDeleteRedaction = (pageNum, rectIndex) => {
    setRedactions((prev) => {
      const entry = prev.find((r) => r.pageIndex === pageNum);
      if (!entry) return prev;
      const newRects = entry.rects.filter((_, i) => i !== rectIndex);
      if (newRects.length === 0) return prev.filter((r) => r.pageIndex !== pageNum);
      return prev.map((r) => (r.pageIndex === pageNum ? { ...r, rects: newRects } : r));
    });
  };

  const clearAllRedactions = () => setRedactions([]);

  const totalRedactionCount = redactions.reduce((n, r) => n + (r.rects?.length || 0), 0);

  // ── LLM Suggestion: get checkbox categories ───────────────────────────────
  const handleGetSuggestions = useCallback(async () => {
    if (!pdf || !numPages) return;
    setSuggestionError(null);
    setLoadingSuggestions(true);
    try {
      let protocolText = '';
      for (let i = 1; i <= numPages; i++) {
        const page = await pdf.getPage(i);
        protocolText += `\n--- Page ${i} ---\n${await getPageText(page)}`;
      }
      const res = await api.post('/export/llm-redact-suggestions-categories', {
        context: llmContext,
        protocolText,
      });
      if (res.data?.success && Array.isArray(res.data.suggestions)) {
        setSuggestions(res.data.suggestions);
        setSelectedSuggestionIds(new Set());
      } else {
        throw new Error(res.data?.message || 'No suggestions returned');
      }
    } catch (e) {
      setSuggestionError(e.response?.data?.message || e.message || 'Failed to get suggestions');
      setSuggestions([]);
    } finally {
      setLoadingSuggestions(false);
    }
  }, [pdf, numPages, llmContext]);

  // ── LLM Suggestion: apply selected categories → phrases → redaction rects ──
  const handleApplySelectedSuggestions = useCallback(async () => {
    if (!pdf || !numPages || selectedSuggestionIds.size === 0) return;
    setSuggestionError(null);
    setApplyingSuggestions(true);
    try {
      const selected = suggestions.filter((s) => selectedSuggestionIds.has(s.id));
      if (!selected.length) return;

      const pages = [];
      for (let i = 1; i <= numPages; i++) {
        const page = await pdf.getPage(i);
        pages.push({ pageIndex: i, text: await getPageText(page) });
      }

      const newRectsByPage = {};

      // Call LLM once per selected category so we never request unselected ones.
      for (const s of selected) {
        const instruction = `${llmContext.trim()}

You MUST ONLY redact content that belongs to this single redaction category: "${s.label}".
Ignore all other categories, even if they appear in the strategy. Return EXACT phrases as they appear in the text.`;

        const res = await api.post('/export/llm-redact-suggestions', {
          instruction,
          pages,
        });
        if (!res.data?.success || !Array.isArray(res.data.pages)) {
          // Skip this category but keep others
          // eslint-disable-next-line no-console
          console.error('LLM redact suggestions returned no pages for category', s.label, res.data);
          continue;
        }

        for (const { pageIndex, phrases } of res.data.pages) {
          if (!phrases?.length) continue;
          const page = await pdf.getPage(pageIndex);
          for (const phrase of phrases) {
            const rects = await phraseToRectsOnPage(page, phrase);
            if (!newRectsByPage[pageIndex]) newRectsByPage[pageIndex] = [];
            newRectsByPage[pageIndex].push(...rects);
          }
        }
      }

      setRedactions((prev) => {
        const next = [...prev];
        for (const [pageIndexStr, rects] of Object.entries(newRectsByPage)) {
          const pageIndex = Number(pageIndexStr);
          const existing = next.find((r) => r.pageIndex === pageIndex);
          const merged = [...(existing?.rects || []), ...rects];
          const without = next.filter((r) => r.pageIndex !== pageIndex);
          next.length = 0;
          next.push(...without, { pageIndex, rects: merged });
        }
        return next.sort((a, b) => a.pageIndex - b.pageIndex);
      });
    } catch (e) {
      setSuggestionError(e.response?.data?.message || e.message || 'Failed to apply suggestions');
    } finally {
      setApplyingSuggestions(false);
    }
  }, [pdf, numPages, llmContext, suggestions, selectedSuggestionIds]);

  const toggleSuggestion = (id) => {
    setSelectedSuggestionIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  // ── navigation ────────────────────────────────────────────────────────────
  const scrollToPage = (n) => {
    const el = document.getElementById(`pdf-page-${n}`);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    setCurrentPage(n);
  };

  if (!projectId) {
    return (
      <Box sx={{ p: 2 }}>
        <Alert severity="info">Select a study / project to view the protocol PDF.</Alert>
      </Box>
    );
  }

  if (loading) {
    return (
      <Box sx={{ p: 4, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
        <CircularProgress />
        <Typography>Loading protocol PDF…</Typography>
      </Box>
    );
  }

  if (error && !pdf) {
    return (
      <Box sx={{ p: 2 }}>
        <Alert severity="warning" sx={{ mb: 1 }}>{error}</Alert>
        <Typography variant="body2" color="text.secondary">
          Export the protocol as PDF (Native) from the Protocol Editor, then return here to view and add redactions.
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* Toolbar */}
      <Paper variant="outlined" sx={{ p: 1, mb: 1, borderRadius: 1 }}>
        <Stack direction="row" alignItems="center" flexWrap="wrap" gap={1}>
          {/* Page navigation */}
          <Tooltip title="Previous page">
            <span>
              <IconButton size="small" onClick={() => scrollToPage(Math.max(1, currentPage - 1))} disabled={currentPage <= 1 || !pdf}>
                <ArrowBackIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          <Typography variant="body2" sx={{ minWidth: 80, textAlign: 'center' }}>
            {pdf ? `${currentPage} / ${numPages}` : '—'}
          </Typography>
          <Tooltip title="Next page">
            <span>
              <IconButton size="small" onClick={() => scrollToPage(Math.min(numPages, currentPage + 1))} disabled={currentPage >= numPages || !pdf}>
                <ArrowForwardIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>

          <Box sx={{ width: 1, borderLeft: '1px solid #ddd', height: 24, mx: 0.5 }} />

          {/* Zoom */}
          <Tooltip title="Zoom out">
            <span>
              <IconButton size="small" onClick={() => setScale((s) => Math.max(SCALE_MIN, +(s - SCALE_STEP).toFixed(2)))} disabled={scale <= SCALE_MIN}>
                <ZoomOutIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          <Typography variant="body2" sx={{ minWidth: 44, textAlign: 'center' }}>{Math.round(scale * 100)}%</Typography>
          <Tooltip title="Zoom in">
            <span>
              <IconButton size="small" onClick={() => setScale((s) => Math.min(SCALE_MAX, +(s + SCALE_STEP).toFixed(2)))} disabled={scale >= SCALE_MAX}>
                <ZoomInIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>

          <Box sx={{ width: 1, borderLeft: '1px solid #ddd', height: 24, mx: 0.5 }} />

          {/* Redaction controls */}
          <Stack direction="row" alignItems="center" gap={0.75}>
            <BlockIcon sx={{ fontSize: 18, color: '#555' }} />
            <Typography variant="body2" sx={{ mr: 0.5 }}>
              Click &amp; drag on the PDF to redact. Hover a black box to delete it.
            </Typography>
            {totalRedactionCount > 0 && (
              <Chip
                label={`${totalRedactionCount} redaction${totalRedactionCount !== 1 ? 's' : ''}`}
                size="small"
                color="default"
                sx={{ fontSize: 11 }}
              />
            )}
            {saving && <CircularProgress size={14} sx={{ ml: 0.5 }} />}
            {totalRedactionCount > 0 && (
              <Tooltip title="Remove all redactions">
                <Button size="small" color="error" variant="outlined" onClick={clearAllRedactions} sx={{ ml: 0.5, py: 0.25 }}>
                  Clear all
                </Button>
              </Tooltip>
            )}
          </Stack>

          {loadingRedactions && (
            <Stack direction="row" alignItems="center" gap={0.5}>
              <CircularProgress size={14} />
              <Typography variant="caption">Loading redactions…</Typography>
            </Stack>
          )}
        </Stack>
      </Paper>

      {error && (
        <Alert severity="warning" sx={{ mb: 1 }} onClose={() => setError(null)}>{error}</Alert>
      )}

      {/* ── Export / access redacted PDF ───────────────────────────────── */}
      <Paper variant="outlined" sx={{ p: 1.5, mb: 1, borderRadius: 1, bgcolor: '#fafbff' }}>
        <Stack direction="row" alignItems="center" flexWrap="wrap" gap={1.5}>
          <Stack direction="row" alignItems="center" gap={0.75}>
            <SaveAltIcon sx={{ fontSize: 18, color: 'primary.main' }} />
            <Typography variant="body2" fontWeight="medium">Redacted PDF</Typography>
          </Stack>

          <Typography variant="body2" color="text.secondary" sx={{ flex: 1, minWidth: 200 }}>
            Click <strong>Generate &amp; Save</strong> to burn redactions into the PDF and store it in Azure — accessible from anywhere via the download / open button.
          </Typography>

          <Button
            variant="contained"
            size="small"
            startIcon={burning ? <CircularProgress size={14} color="inherit" /> : <SaveAltIcon />}
            onClick={handleBurnRedactedPdf}
            disabled={burning || !pdf}
          >
            {burning ? 'Rasterizing redacted pages…' : 'Generate & Save'}
          </Button>

          {redactedPdfUrl && (
            <>
              <Stack direction="row" alignItems="center" gap={0.5}>
                <CheckCircleIcon sx={{ fontSize: 16, color: 'success.main' }} />
                <Typography variant="caption" color="success.main">
                  Saved{redactedAt ? ` · ${redactedAt.toLocaleDateString()} ${redactedAt.toLocaleTimeString()}` : ''}
                </Typography>
              </Stack>
              <Tooltip title="Open redacted PDF in new tab">
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<OpenInNewIcon />}
                  onClick={() => window.open(redactedPdfUrl, '_blank', 'noopener,noreferrer')}
                >
                  Open
                </Button>
              </Tooltip>
              <Tooltip title="Download redacted PDF">
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<DownloadIcon />}
                  onClick={handleDownloadRedactedPdf}
                >
                  Download
                </Button>
              </Tooltip>
            </>
          )}
        </Stack>
        {burnError && (
          <Alert severity="error" sx={{ mt: 1 }} onClose={() => setBurnError(null)}>{burnError}</Alert>
        )}
      </Paper>

      {/* ── LLM Suggestion panel ───────────────────────────────────────────── */}
      <Paper variant="outlined" sx={{ mb: 1, borderRadius: 1 }}>
        <Button
          fullWidth
          onClick={() => setLlmSuggestionOpen((o) => !o)}
          startIcon={<LightbulbIcon />}
          endIcon={llmSuggestionOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          sx={{ justifyContent: 'space-between', textTransform: 'none', py: 1.25 }}
        >
          <Typography variant="subtitle2">LLM Suggestion</Typography>
          <Typography variant="caption" color="text.secondary">
            Use context + protocol to get redaction suggestions, then apply selected as black boxes
          </Typography>
        </Button>
        <Collapse in={llmSuggestionOpen}>
          <Box sx={{ p: 2, pt: 0, borderTop: 1, borderColor: 'divider' }}>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
              Redaction strategy (context) — edit if needed:
            </Typography>
            <TextField
              fullWidth
              multiline
              minRows={4}
              maxRows={8}
              value={llmContext}
              onChange={(e) => setLlmContext(e.target.value)}
              size="small"
              placeholder="Paste redaction strategy / context..."
              sx={{ mb: 2, fontFamily: 'monospace', fontSize: 12 }}
            />
            <Stack direction="row" alignItems="center" flexWrap="wrap" gap={1} sx={{ mb: 2 }}>
              <Button
                size="small"
                variant="outlined"
                onClick={handleGetSuggestions}
                disabled={loadingSuggestions || !pdf}
                startIcon={loadingSuggestions ? <CircularProgress size={14} /> : <LightbulbIcon />}
              >
                {loadingSuggestions ? 'Getting suggestions…' : 'Get suggestions'}
              </Button>
              {suggestions.length > 0 && (
                <Typography variant="body2" color="text.secondary">
                  {suggestions.length} categor{suggestions.length !== 1 ? 'ies' : 'y'} — select below, then Apply
                </Typography>
              )}
            </Stack>
            {suggestions.length > 0 && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                  Select categories to redact:
                </Typography>
                <Stack direction="column" sx={{ maxHeight: 200, overflow: 'auto', flexWrap: 'wrap' }}>
                  {suggestions.map((s) => (
                    <FormControlLabel
                      key={s.id}
                      control={
                        <Checkbox
                          size="small"
                          checked={selectedSuggestionIds.has(s.id)}
                          onChange={() => toggleSuggestion(s.id)}
                        />
                      }
                      label={
                        <Typography variant="body2">
                          {s.label}
                          {s.description ? ` — ${s.description}` : ''}
                        </Typography>
                      }
                    />
                  ))}
                </Stack>
                <Button
                  size="small"
                  variant="contained"
                  onClick={handleApplySelectedSuggestions}
                  disabled={applyingSuggestions || selectedSuggestionIds.size === 0}
                  startIcon={applyingSuggestions ? <CircularProgress size={14} color="inherit" /> : null}
                  sx={{ mt: 1 }}
                >
                  {applyingSuggestions ? 'Applying…' : `Apply selected (${selectedSuggestionIds.size})`}
                </Button>
              </Box>
            )}
            {suggestionError && (
              <Alert severity="error" sx={{ mt: 1 }} onClose={() => setSuggestionError(null)}>
                {suggestionError}
              </Alert>
            )}
          </Box>
        </Collapse>
      </Paper>

      {/* PDF canvas area */}
      <Box
        ref={scrollRef}
        sx={{ flex: 1, overflow: 'auto', bgcolor: '#888', p: 1, borderRadius: 1 }}
        onMouseLeave={() => { if (drawing) { setDrawing(false); setDrawStart(null); setCurrentRect(null); } }}
      >
        {pdf && Array.from({ length: numPages }, (_, i) => i + 1).map((pageNum) => (
          <Box
            key={pageNum}
            id={`pdf-page-${pageNum}`}
            onMouseEnter={() => setCurrentPage(pageNum)}
          >
            <PdfPage
              pdf={pdf}
              pageNum={pageNum}
              scale={scale}
              pageRedactions={redactions.find((r) => r.pageIndex === pageNum)}
              drawing={drawing}
              drawingPage={drawingPage}
              currentRect={currentRect}
              onMouseDown={handleMouseDown}
              onMouseMove={handleMouseMove}
              onMouseUp={handleMouseUp}
              onDeleteRedaction={handleDeleteRedaction}
            />
          </Box>
        ))}
      </Box>
    </Box>
  );
};

export default PdfViewersTab;
